import { useEffect } from 'react'
import debounce from 'lodash/debounce'
import { LocalStorage } from '@ztechapps/common.utils'

const STORAGE_PREFIX = 'modal-size:'
const PERSIST_DEBOUNCE_MS = 300

type StoredSize = { width: string; height: string }

const storage = new LocalStorage<StoredSize>()

/**
 * Persists a `resizable` modal's user-driven size to localStorage and restores it on next open.
 *
 * Looks up the dialog via `.modal-dialog.${resizableClass} .modal-content` (react-bootstrap
 * portals the modal into document.body, so a ref on the React tree doesn't reach the content
 * node). Polls via `requestAnimationFrame` until the portal mounts.
 *
 * No-op when any of `storageKey` / `isOpen` / `resizableClass` is falsy — the caller toggles
 * stickiness by passing or omitting `storageKey`.
 */
export const useStickyModalSize = (storageKey: string | undefined, isOpen: boolean, resizableClass: string) => {
  useEffect(() => {
    if (!storageKey || !isOpen || !resizableClass) return

    let cancelled = false
    let observer: ResizeObserver | undefined
    let persistDebounced: ReturnType<typeof debounce> | undefined

    const attach = () => {
      if (cancelled) return

      const content = document.querySelector<HTMLElement>(`.modal-dialog.${resizableClass} .modal-content`)
      if (!content) {
        requestAnimationFrame(attach)
        return
      }

      // Restore previously-stored size BEFORE attaching the observer, so the observer's
      // first callback runs against the restored state (no spurious overwrite of default size).
      const stored = storage.get(`${STORAGE_PREFIX}${storageKey}`, true) as StoredSize | null
      if (stored?.width && stored?.height) {
        content.style.width = stored.width
        content.style.height = stored.height
      }

      // Persist size on resize (debounced — ResizeObserver fires per frame during drag).
      persistDebounced = debounce(() => {
        const width = content.style.width || `${content.offsetWidth}px`
        const height = content.style.height || `${content.offsetHeight}px`
        storage.set(`${STORAGE_PREFIX}${storageKey}`, { width, height })
      }, PERSIST_DEBOUNCE_MS)

      observer = new ResizeObserver(persistDebounced)
      observer.observe(content)
    }

    attach()

    return () => {
      cancelled = true
      observer?.disconnect()
      persistDebounced?.cancel()
    }
  }, [storageKey, isOpen, resizableClass])
}
